/**
 * Performance logging utility for frontend
 * Helps identify slow API calls and rendering
 */

const SLOW_THRESHOLD_MS = 500;
const VERY_SLOW_THRESHOLD_MS = 2000;

// Store timing data
const timingHistory = [];
const MAX_HISTORY = 100;

/**
 * Log a performance timing
 */
export function logTiming(operation, durationMs, details = {}) {
  const entry = {
    timestamp: new Date().toISOString(),
    operation,
    durationMs: Math.round(durationMs),
    details,
    slow: durationMs >= SLOW_THRESHOLD_MS,
    verySlow: durationMs >= VERY_SLOW_THRESHOLD_MS
  };
  
  // Keep history limited
  timingHistory.push(entry);
  if (timingHistory.length > MAX_HISTORY) {
    timingHistory.shift();
  }
  
  // Console logging with color coding
  if (entry.verySlow) {
    console.warn(`🔴 VERY SLOW ${operation}: ${entry.durationMs}ms`, details);
  } else if (entry.slow) {
    console.warn(`🟡 SLOW ${operation}: ${entry.durationMs}ms`, details);
  } else {
    console.log(`🟢 ${operation}: ${entry.durationMs}ms`, details);
  }
  
  return entry;
}

/**
 * Time an async operation
 */
export async function timeAsync(operation, asyncFn, details = {}) {
  const start = performance.now();
  try {
    const result = await asyncFn();
    const duration = performance.now() - start;
    logTiming(operation, duration, { ...details, success: true });
    return result;
  } catch (error) {
    const duration = performance.now() - start;
    logTiming(operation, duration, { ...details, success: false, error: error.message });
    throw error;
  }
}

/**
 * Create a timed axios wrapper
 */
export function createTimedAxios(axios) {
  const timedAxios = {
    async get(url, config) {
      return timeAsync(`API GET ${url.split('?')[0]}`, () => axios.get(url, config), { url });
    },
    async post(url, data, config) {
      return timeAsync(`API POST ${url.split('?')[0]}`, () => axios.post(url, data, config), { url });
    },
    async put(url, data, config) {
      return timeAsync(`API PUT ${url.split('?')[0]}`, () => axios.put(url, data, config), { url });
    },
    async delete(url, config) {
      return timeAsync(`API DELETE ${url.split('?')[0]}`, () => axios.delete(url, config), { url });
    }
  };
  return timedAxios;
}

/**
 * Get timing history for analysis
 */
export function getTimingHistory() {
  return [...timingHistory];
}

/**
 * Get slow requests from history
 */
export function getSlowRequests() {
  return timingHistory.filter(t => t.slow);
}

/**
 * Get performance summary
 */
export function getPerformanceSummary() {
  if (timingHistory.length === 0) {
    return { message: 'No timing data yet' };
  }
  
  const apiCalls = timingHistory.filter(t => t.operation.startsWith('API'));
  const avgDuration = apiCalls.reduce((sum, t) => sum + t.durationMs, 0) / apiCalls.length;
  const slowCount = apiCalls.filter(t => t.slow).length;
  const verySlowCount = apiCalls.filter(t => t.verySlow).length;
  
  return {
    totalCalls: apiCalls.length,
    avgDurationMs: Math.round(avgDuration),
    slowCount,
    verySlowCount,
    slowestCalls: apiCalls
      .sort((a, b) => b.durationMs - a.durationMs)
      .slice(0, 5)
      .map(t => ({ operation: t.operation, durationMs: t.durationMs }))
  };
}

/**
 * Time page load
 */
export function timePageLoad(pageName) {
  const start = performance.now();
  
  return {
    markReady: (details = {}) => {
      const duration = performance.now() - start;
      logTiming(`Page Load: ${pageName}`, duration, details);
    }
  };
}

/**
 * Print performance summary to console
 */
export function printPerformanceSummary() {
  const summary = getPerformanceSummary();
  console.group('📊 Performance Summary');
  console.log('Total API calls:', summary.totalCalls);
  console.log('Average duration:', summary.avgDurationMs, 'ms');
  console.log('Slow calls (>500ms):', summary.slowCount);
  console.log('Very slow calls (>2000ms):', summary.verySlowCount);
  console.log('Slowest calls:', summary.slowestCalls);
  console.groupEnd();
}

// Make available globally for debugging
if (typeof window !== 'undefined') {
  window.__perfLogger = {
    getTimingHistory,
    getSlowRequests,
    getPerformanceSummary,
    printPerformanceSummary
  };
  console.log('📊 Performance logger loaded. Use window.__perfLogger.printPerformanceSummary() to see stats.');
}

export default {
  logTiming,
  timeAsync,
  createTimedAxios,
  getTimingHistory,
  getSlowRequests,
  getPerformanceSummary,
  timePageLoad,
  printPerformanceSummary
};
